import { useTranslation } from 'react-i18next'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@structural-climbing/ui'
import { softBadgeColors, StarRating } from '@structural-climbing/ui'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import { ROUTE_COLORS } from './constants'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import type { ClimbingRoute } from './types'
import { CommentIcon } from './CommentIcon'

interface RouteDetailModalProps {
  route: ClimbingRoute | null
  apiUrl: string
  onClose: () => void
  onEdit: (route: ClimbingRoute) => void
  onViewComments: (route: ClimbingRoute) => void
}

export function RouteDetailModal({
  route,
  apiUrl,
  onClose,
  onEdit,
  onViewComments,
}: RouteDetailModalProps) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()

  return (
    <Modal
      isOpen={!!route}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => {
          if (!route) return null
          const colorLabel = ROUTE_COLORS.find((c) => c.value === route.color)?.label
          const grade = gradeDisplay(
            gradeConfig,
            route.grade,
            disciplineForSection(route.section?.discipline),
          )
          const badge = softBadgeColors(grade.color)
          return (
            <>
              <ModalHeader>{t('routes.viewRoute')}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  {route.image_r2_key ? (
                    <img
                      src={`${apiUrl}/climbing-routes/${route.id}/image`}
                      alt={`${t('routes.grade')} ${grade.label}`}
                      className="max-h-[60vh] w-full rounded-lg object-contain"
                    />
                  ) : (
                    <div className="flex h-32 w-full items-center justify-center rounded-lg bg-default-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 text-foreground/20"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <span
                      className="rounded px-2.5 py-1 text-sm font-semibold"
                      style={{ backgroundColor: badge.bg, color: badge.fg }}
                    >
                      {grade.label}
                    </span>
                    <span
                      className="h-4 w-4 flex-none rounded-full border border-black/10 shadow"
                      style={{ backgroundColor: route.color ?? '#d4d4d8' }}
                    />
                    {colorLabel && <span className="text-sm text-foreground/60">{colorLabel}</span>}
                    <span className="ml-auto text-sm text-foreground/50">
                      {t('routes.ascentCount', { count: route.ascent_count })}
                    </span>
                  </div>

                  {route.avg_rating != null && (
                    <div className="flex items-center gap-2">
                      <StarRating
                        value={route.avg_rating}
                        showValue
                        size="sm"
                        countLabel={t('routes.ratingCount', { count: route.rating_count })}
                        label={t('routes.ratingOutOf', { value: route.avg_rating.toFixed(1) })}
                      />
                    </div>
                  )}

                  {route.setter?.name && (
                    <p className="text-sm text-foreground/60">
                      {t('routes.setterLabel', { name: route.setter.name })}
                    </p>
                  )}

                  {route.setter_note && (
                    <div className="rounded-lg bg-content2 p-3">
                      <p className="mb-1 text-xs font-semibold text-foreground/60">
                        {t('routes.setterNoteTitle')}
                      </p>
                      <p className="whitespace-pre-line text-sm text-foreground/80">
                        {route.setter_note}
                      </p>
                    </div>
                  )}

                  <Button
                    variant="flat"
                    className="justify-start"
                    startContent={<CommentIcon />}
                    onClick={() => onViewComments(route)}
                  >
                    {t('routes.commentsButton', { count: route.comment_count })}
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    onEdit(route)
                    onClose()
                  }}
                >
                  {t('routes.edit')}
                </Button>
              </ModalFooter>
            </>
          )
        }}
      </ModalContent>
    </Modal>
  )
}
